import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import {
  type Attempt,
  type SessionResult,
  SessionRecorder,
  recordAttempt,
  statsFor,
} from "@nav-games/games-core";
import {
  SCENARIO_IDS,
  SCENARIOS,
  vesselTypeLabel,
  type Scenario,
} from "@nav-games/irpcs";

import { HelpButton, HelpPanel } from "../../ui/HelpPanel";
import { ScenarioFigure } from "../../ui/ScenarioFigure";

interface ScenariosProps {
  onNavigate: (to: string) => void;
}

const MODULE = "irpcs";
const GAME = "scenarios";
const CARDS_PER_SESSION = 8;

interface Card {
  scenario: Scenario;
}

/// Scenarios reachable only as a follow-up continuation of another
/// (via `action.followUpId`). They're kept out of the cold-draw deck
/// so a mid-chain card never opens a session out of context — they
/// only appear when you've correctly answered their parent.
const FOLLOW_UP_IDS = new Set(
  Object.values(SCENARIOS).flatMap((s) =>
    s.actions.flatMap((a) => (a.followUpId ? [a.followUpId] : [])),
  ),
);

/// The pool we deal from. Drawing without replacement (a shuffled
/// deck) guarantees no repeats within a session — CARDS_PER_SESSION
/// is far smaller than this, so every card in a session is distinct.
const DECK_IDS: readonly string[] = SCENARIO_IDS.filter(
  (id) => !FOLLOW_UP_IDS.has(id),
);

function shuffled(ids: readonly string[]): string[] {
  const a = [...ids];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j]!, a[i]!];
  }
  return a;
}

/// Pop the next id from the deck, refilling (reshuffling) when empty.
/// `avoid` prevents the same scenario landing across a reshuffle
/// boundary. Mutates `deck` in place.
function draw(deck: string[], avoid?: string): string {
  if (deck.length === 0) {
    deck.push(...shuffled(DECK_IDS));
    if (avoid && deck[0] === avoid && deck.length > 1) {
      [deck[0], deck[1]] = [deck[1]!, deck[0]!];
    }
  }
  return deck.shift()!;
}

export function Scenarios({ onNavigate }: ScenariosProps) {
  const recorder = useMemo(
    () =>
      new SessionRecorder({
        module: MODULE,
        game: GAME,
        length: CARDS_PER_SESSION,
      }),
    [],
  );

  // The shuffled deck we deal from, persisted across renders. Drawing
  // without replacement is what stops scenarios repeating in a session.
  const deck = useRef<string[]>(shuffled(DECK_IDS));
  const [index, setIndex] = useState(0);
  const [card, setCard] = useState<Card>(() => ({
    scenario: SCENARIOS[draw(deck.current)]!,
  }));
  const [shownAt, setShownAt] = useState<number>(() => Date.now());
  const [chosen, setChosen] = useState<string | null>(null);
  const [result, setResult] = useState<SessionResult | null>(null);
  const [helpOpen, setHelpOpen] = useState(false);
  const sessionStartedAt = useRef<number>(Date.now());

  const advance = useCallback(
    (followUpId?: string) => {
      if (index + 1 >= CARDS_PER_SESSION) {
        setResult(recorder.summarise());
        return;
      }
      setIndex((i) => i + 1);
      if (followUpId && SCENARIOS[followUpId]) {
        setCard({ scenario: SCENARIOS[followUpId]! });
      } else {
        setCard((c) => ({
          scenario: SCENARIOS[draw(deck.current, c.scenario.id)]!,
        }));
      }
      setShownAt(Date.now());
      setChosen(null);
    },
    [index, recorder],
  );

  const onChoose = useCallback(
    (actionId: string) => {
      if (chosen || result) return;
      const ms = Date.now() - shownAt;
      const action = card.scenario.actions.find((a) => a.id === actionId)!;
      const ok = action.correct;
      const attempt: Omit<Attempt, "module"> = {
        char: card.scenario.id,
        ok,
        ms,
        ts: Date.now(),
      };
      recorder.log(attempt);
      recordAttempt({ ...attempt, module: MODULE });
      setChosen(actionId);
    },
    [card, chosen, recorder, result, shownAt],
  );

  if (result) {
    return (
      <EndCard
        result={result}
        sessionStartMs={sessionStartedAt.current}
        onAgain={() => {
          deck.current = shuffled(DECK_IDS);
          setIndex(0);
          setCard({ scenario: SCENARIOS[draw(deck.current)]! });
          setShownAt(Date.now());
          setChosen(null);
          setResult(null);
          sessionStartedAt.current = Date.now();
        }}
        onBack={() => onNavigate("/play/irpcs")}
      />
    );
  }

  const scenario = card.scenario;
  const revealed = chosen !== null;
  const chosenAction = scenario.actions.find((a) => a.id === chosen);
  const correctAction = scenario.actions.find((a) => a.correct)!;

  return (
    <>
      <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 8 }}>
        <HelpButton onOpen={() => setHelpOpen(true)} />
      </div>
      <HelpPanel section={helpOpen ? "irpcs" : null} onClose={() => setHelpOpen(false)} />
      <p className="small muted">
        Scenario {index + 1} of {CARDS_PER_SESSION}
      </p>

      <div className="card">
        <h3 style={{ marginTop: 0 }}>{scenario.title}</h3>
        <p className="small muted" style={{ marginBottom: 12 }}>
          {scenario.setup}
        </p>

        <div style={{ display: "flex", justifyContent: "center" }}>
          <ScenarioFigure scenario={scenario} revealed={revealed} size={320} />
        </div>

        <div
          style={{
            display: "flex",
            justifyContent: "center",
            gap: 12,
            flexWrap: "wrap",
            marginTop: 8,
          }}
        >
          {scenario.vessels.map((v) => (
            <div key={v.label} className="small muted">
              <strong>{v.label}:</strong> {vesselTypeLabel(v.type)}
            </div>
          ))}
        </div>

        {revealed && (
          <div
            role="status"
            aria-live="polite"
            style={{
              marginTop: 16,
              padding: 12,
              borderRadius: 6,
              background: chosenAction!.correct
                ? "rgba(44, 122, 59, 0.10)"
                : "rgba(184, 48, 42, 0.10)",
            }}
          >
            <p style={{ margin: 0, fontWeight: 600 }}>
              {chosenAction!.correct ? "✓ " : "✗ "}
              {chosenAction!.correct
                ? "Correct."
                : `Not quite — the right answer is "${correctAction.label}".`}
            </p>
            <p className="small muted" style={{ margin: "8px 0 0" }}>
              <strong>Rule:</strong> {scenario.rule}
            </p>
            <p className="small muted" style={{ margin: "6px 0 0" }}>
              {scenario.explanation}
            </p>
            <button
              onClick={() => advance(chosenAction!.followUpId)}
              style={{ marginTop: 12, width: "100%" }}
            >
              {chosenAction!.followUpId && chosenAction!.correct
                ? "Continue scenario →"
                : "Next scenario →"}
            </button>
          </div>
        )}

        {!revealed && (
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr",
              gap: 8,
              marginTop: 16,
            }}
          >
            {scenario.actions.map((a) => (
              <button
                key={a.id}
                className="secondary"
                onClick={() => onChoose(a.id)}
                style={{
                  fontSize: 14,
                  padding: "12px",
                  textAlign: "left",
                }}
              >
                {a.label}
              </button>
            ))}
          </div>
        )}
      </div>
    </>
  );
}

function EndCard({
  result,
  sessionStartMs,
  onAgain,
  onBack,
}: {
  result: SessionResult;
  sessionStartMs: number;
  onAgain: () => void;
  onBack: () => void;
}) {
  const elapsedS = Math.round((Date.now() - sessionStartMs) / 1000);
  const rolling = SCENARIO_IDS
    .map((id) => ({ id, ...statsFor(MODULE, id) }))
    .filter((s) => s.attempts >= 2)
    .sort((a, b) => a.hitRate - b.hitRate);
  const weakest = rolling.slice(0, 5);

  return (
    <>
      <h2>Session complete</h2>
      <div className="card">
        <p>
          <strong>{result.correct}</strong> / {result.total} correct
          {result.total > 0 && (
            <> ({Math.round((result.correct / result.total) * 100)}%)</>
          )}{" "}
          in {elapsedS}s.
        </p>
      </div>
      {weakest.length > 0 && (
        <div className="card">
          <h3>Weakest scenarios (rolling)</h3>
          <ul className="small muted">
            {weakest.map((w) => (
              <li key={w.id}>
                {SCENARIOS[w.id]?.title ?? w.id} —{" "}
                {Math.round(w.hitRate * 100)}% over {w.attempts} attempts
              </li>
            ))}
          </ul>
        </div>
      )}
      <div
        className="row"
        style={{ gap: 8, flexDirection: "column", alignItems: "stretch" }}
      >
        <button onClick={onAgain}>Play another</button>
        <button className="secondary" onClick={onBack}>
          ← Back to IRPCS
        </button>
      </div>
    </>
  );
}
